import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faSave, faMapMarkerAlt, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { UI_COLORS, colorHelpers } from '../../utils/colors';
import { useTranslation } from 'react-i18next';
import { extractApiError } from '../../utils/apiError';
import Map from '../common/Map';

/**
 * LocationForm Component
 * Modal form for creating/editing a location using the app's form styling
 */
/**
 * LocationForm props
 * @param {{isOpen: boolean, onClose: function, onSubmit: function, location: object|null, loading: boolean}} props
 */
const LocationForm = ({
  isOpen,
  onClose,
  onSubmit,
  location = null,
  loading = false,
}) => {
  const { t } = useTranslation();
  const strings = t('locations', { returnObjects: true });

  const [formData, setFormData] = useState({
    country: t('locations.defaults.country'),
    state: '',
    city: '',
    delivery_location: '',
    map_url: '',
  });

  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  const dropRef = useRef(null);
  const [showMapModal, setShowMapModal] = useState(false);
  const [isStateDropdownOpen, setIsStateDropdownOpen] = useState(false);
  const stateDropdownRef = useRef(null);

  useEffect(() => {
    if (location) {
      setFormData({
        country: location.country || t('locations.defaults.country'),
        state: location.state || '',
        city: location.city || '',
        delivery_location: location.delivery_location || '',
        map_url: location.map_url || '',
      });
    } else {
      setFormData({
        country: t('locations.defaults.country'),
        state: '',
        city: '',
        delivery_location: '',
        map_url: '',
      });
    }
    setErrors({});
    setSubmitError('');
  }, [location, isOpen, t]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (submitError) setSubmitError('');

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  // Custom dropdown handlers
  const handleStateSelect = (stateLabel) => {
    setFormData((prev) => ({ ...prev, state: stateLabel }));
    setIsStateDropdownOpen(false);
    if (errors.state) {
      setErrors((prev) => ({ ...prev, state: '' }));
    }
  };

  const toggleStateDropdown = () => {
    setIsStateDropdownOpen(!isStateDropdownOpen);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (stateDropdownRef.current && !stateDropdownRef.current.contains(event.target)) {
        setIsStateDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Handle keyboard navigation
  const handleStateDropdownKeyDown = (event) => {
    if (event.key === 'Escape') {
      setIsStateDropdownOpen(false);
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      toggleStateDropdown();
    }
  };

  // Helpers to build map URL from address or coordinates
  const buildMapUrlFromAddress = (address) => {
    if (!address) return '';
    return `https://maps.google.com/?q=${encodeURIComponent(address)}`;
  };

  const buildMapUrlFromCoords = (lat, lng) => {
    return `https://maps.google.com/?q=${lat},${lng}`;
  };

  const parseDroppedText = (text) => {
    if (!text) return {};
    const trimmed = text.trim();

    // If it's a google maps url with @lat,lng pattern
    const atCoordMatch = trimmed.match(/@(-?\d+\.\d+),(-?\d+\.\d+)/);
    if (atCoordMatch) {
      const lat = atCoordMatch[1];
      const lng = atCoordMatch[2];
      return { delivery_location: `${lat},${lng}`, map_url: buildMapUrlFromCoords(lat, lng) };
    }

    // If the url has q= param
    try {
      const url = new URL(trimmed);
      const q = url.searchParams.get('q');
      if (q) {
        return { delivery_location: q, map_url: buildMapUrlFromAddress(q) };
      }
      // Some map URLs include coordinates in the path after /@
      const pathCoord = (url.pathname || '').match(/@(-?\d+\.\d+),(-?\d+\.\d+)/);
      if (pathCoord) {
        const lat = pathCoord[1];
        const lng = pathCoord[2];
        return { delivery_location: `${lat},${lng}`, map_url: buildMapUrlFromCoords(lat, lng) };
      }
    } catch (e) {
      // not a URL, fallthrough
    }

    // If text is plain coordinates like "12.34,56.78"
    const coordOnly = trimmed.match(/^(-?\d+\.\d+)\s*,\s*(-?\d+\.\d+)$/);
    if (coordOnly) {
      const lat = coordOnly[1];
      const lng = coordOnly[2];
      return { delivery_location: `${lat},${lng}`, map_url: buildMapUrlFromCoords(lat, lng) };
    }

    // Treat remaining text as an address
    return { delivery_location: trimmed, map_url: buildMapUrlFromAddress(trimmed) };
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const text = e.dataTransfer.getData('text') || e.dataTransfer.getData('text/plain') || e.dataTransfer.getData('text/uri-list');
    if (!text) return;
    const parsed = parseDroppedText(text);
    setFormData((prev) => ({ ...prev, ...parsed }));
    setErrors((prev) => ({ ...prev, delivery_location: '', map_url: '' }));
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const openPreviewMap = () => {
    setShowMapModal(true);
  };

  // Handle location selection from map - only update map URL, preserve other fields
  const handleLocationSelect = (locationData) => {
    if (locationData && locationData.map_url) {
      setFormData((prev) => ({
        ...prev,
        map_url: locationData.map_url, // Only update map URL
      }));
      setErrors((prev) => ({ ...prev, map_url: '' }));
    }
  };

  const closeMapModal = () => setShowMapModal(false);

  // Helper function to parse coordinates from form data
  const parseLatLngFromForm = () => {
    // Try delivery_location for coordinates like "lat,lng"
    const dl = (formData.delivery_location || '').trim();
    const coordOnly = dl.match(/^(-?\d+\.?\d*)\s*,\s*(-?\d+\.?\d*)$/);
    if (coordOnly) return { lat: parseFloat(coordOnly[1]), lng: parseFloat(coordOnly[2]) };
    
    // Try map_url to extract @lat,lng
    try {
      const u = new URL(formData.map_url || '');
      const at = (u.pathname || '').match(/@(-?\d+\.?\d*),(-?\d+\.?\d*)/);
      if (at) return { lat: parseFloat(at[1]), lng: parseFloat(at[2]) };
    } catch (e) {
      // not a url
    }
    
    return null;
  };

  const validateForm = () => {
    const newErrors = {};
    const f = formData;

    if (!f.country || !f.country.trim()) {
      newErrors.country = strings.forms.fields.country.errors.required;
    }
    if (!f.state || !f.state.trim()) {
      newErrors.state = strings.forms.fields.state.errors.required;
    }
    if (!f.city || !f.city.trim()) {
      newErrors.city = strings.forms.fields.city.errors.required;
    }
    if (!f.delivery_location || !f.delivery_location.trim()) {
      newErrors.delivery_location = strings.forms.fields.deliveryLocation.errors.required;
    }

    // If map_url provided, do a simple validation
    if (f.map_url && !/^https?:\/\//i.test(f.map_url)) {
      newErrors.map_url = strings.forms.fields.mapUrl.errors.invalid;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;               // guard double-submit (parent-driven loading)
    if (!validateForm()) return;

    const payload = {
      country: formData.country,
      state: formData.state,
      city: formData.city,
      delivery_location: formData.delivery_location,
      map_url: formData.map_url,
    };

    setSubmitError('');
    try {
      const result = await onSubmit(payload);
      // Only close on success; keep the form open with data on failure.
      if (result && result.success === false) {
        const msg = typeof result.error === 'string' ? result.error : extractApiError(result.error);
        setSubmitError(msg || 'Failed to save location. Please check the details and try again.');
        return;
      }
      onClose();
    } catch (err) {
      setSubmitError(extractApiError(err) || 'Failed to save location. Please check the details and try again.');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: UI_COLORS.BACKGROUNDS.OVERLAY }}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" style={{ backgroundColor: UI_COLORS.BACKGROUNDS.CARD }}>
        <div className="flex items-center justify-between p-6 border-b" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
          <h2 className="text-2xl font-bold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
            {location ? strings.forms.title.edit : strings.forms.title.add}
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg transition-all duration-200 hover:scale-105"
            style={{ color: UI_COLORS.TEXT.MUTED, backgroundColor: colorHelpers.withOpacity(UI_COLORS.TEXT.MUTED, 0.1) }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = colorHelpers.withOpacity(UI_COLORS.TEXT.MUTED, 0.2); }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = colorHelpers.withOpacity(UI_COLORS.TEXT.MUTED, 0.1); }}
          >
            <FontAwesomeIcon icon={faTimes} className="text-lg" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{strings.forms.fields.country.label}</label>
              <input
                name="country"
                value={formData.country}
                onChange={handleChange}
                className="w-full px-4 py-3 border-2 rounded-xl focus:ring-2 transition-all duration-200"
                style={{ backgroundColor: UI_COLORS.FORMS.INPUT.background, borderColor: errors.country ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border, color: UI_COLORS.FORMS.INPUT.text }}
                placeholder={strings.forms.fields.country.placeholder}
              />
              {errors.country && <p className="text-sm font-medium" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.country}</p>}
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{`${strings.forms.fields.state.label} ${t('common.symbols.required')}`}</label>
              {formData.country && formData.country.toLowerCase().includes(t('locations.defaults.country').toLowerCase()) && Array.isArray(strings.states) && strings.states.length > 0 ? (
                <div className="relative" ref={stateDropdownRef}>
                  <button
                    type="button"
                    onClick={toggleStateDropdown}
                    onKeyDown={handleStateDropdownKeyDown}
                    className="w-full px-4 py-3 border-2 rounded-xl focus:ring-2 transition-all duration-200 flex items-center justify-between text-left"
                    style={{
                      backgroundColor: UI_COLORS.FORMS.INPUT.background,
                      borderColor: errors.state ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border,
                      color: formData.state ? UI_COLORS.FORMS.INPUT.text : UI_COLORS.TEXT.MUTED,
                    }}
                    aria-haspopup="listbox"
                    aria-expanded={isStateDropdownOpen}
                  >
                    <span>{formData.state || strings.forms.fields.state.placeholder}</span>
                    <FontAwesomeIcon 
                      icon={faChevronDown} 
                      className={`transition-transform duration-200 ${isStateDropdownOpen ? 'rotate-180' : ''}`}
                      style={{ color: UI_COLORS.TEXT.MUTED }}
                    />
                  </button>
                  
                  {isStateDropdownOpen && (
                    <div 
                      className="absolute z-50 w-full mt-1 bg-white border-2 rounded-xl shadow-lg max-h-60"
                      style={{ 
                        borderColor: UI_COLORS.FORMS.INPUT.border,
                        backgroundColor: UI_COLORS.FORMS.INPUT.background,
                        overflow: 'hidden'
                      }}
                    >
                      <div 
                        className="py-1"
                        style={{
                          maxHeight: '15rem',
                          overflowY: 'auto',
                          scrollbarWidth: 'none',
                          msOverflowStyle: 'none'
                        }}
                      >
                        <style jsx>{`
                          div::-webkit-scrollbar {
                            display: none;
                          }
                        `}</style>
                        {strings.states.map((s) => (
                          <button
                            key={s.value}
                            type="button"
                            onClick={() => handleStateSelect(s.label)}
                            className="w-full px-4 py-2 text-left hover:bg-opacity-10 transition-colors duration-150"
                            style={{
                              color: UI_COLORS.FORMS.INPUT.text,
                              backgroundColor: formData.state === s.label ? colorHelpers.withOpacity(UI_COLORS.BUTTONS.PRIMARY.background, 0.1) : 'transparent'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = colorHelpers.withOpacity(UI_COLORS.BUTTONS.PRIMARY.background, 0.05);
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = formData.state === s.label ? colorHelpers.withOpacity(UI_COLORS.BUTTONS.PRIMARY.background, 0.1) : 'transparent';
                            }}
                          >
                            {s.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <input
                  name="state"
                  value={formData.state}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border-2 rounded-xl focus:ring-2 transition-all duration-200"
                  style={{ backgroundColor: UI_COLORS.FORMS.INPUT.background, borderColor: errors.state ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border, color: UI_COLORS.FORMS.INPUT.text }}
                  placeholder={strings.forms.fields.state.placeholder}
                />
              )}
              {errors.state && <p className="text-sm font-medium" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.state}</p>}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{`${strings.forms.fields.city.label} ${t('common.symbols.required')}`}</label>
              <input
                name="city"
                value={formData.city}
                onChange={handleChange}
                className="w-full px-4 py-3 border-2 rounded-xl focus:ring-2 transition-all duration-200"
                style={{ backgroundColor: UI_COLORS.FORMS.INPUT.background, borderColor: errors.city ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border, color: UI_COLORS.FORMS.INPUT.text }}
                placeholder={strings.forms.fields.city.placeholder}
              />
              {errors.city && <p className="text-sm font-medium" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.city}</p>}
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{`${strings.forms.fields.deliveryLocation.label} ${t('common.symbols.required')}`}</label>
              <div
                ref={dropRef}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                className="w-full px-4 py-3 border-2 rounded-xl transition-all duration-200 flex items-center justify-between"
                style={{ backgroundColor: UI_COLORS.FORMS.INPUT.background, borderColor: errors.delivery_location ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border, color: UI_COLORS.FORMS.INPUT.text }}
              >
                <input
                  name="delivery_location"
                  value={formData.delivery_location}
                  onChange={handleChange}
                  className="flex-1 bg-transparent outline-none"
                  placeholder={strings.forms.fields.deliveryLocation.placeholder}
                />
                <div className="ml-4 text-sm text-gray-400 flex items-center">
                  <FontAwesomeIcon icon={faMapMarkerAlt} />
                </div>
              </div>
              {errors.delivery_location && <p className="text-sm font-medium" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.delivery_location}</p>}
            </div>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{strings.forms.fields.mapUrl.label}</label>
            <div className="flex items-center space-x-3">
              <input
                name="map_url"
                value={formData.map_url}
                onChange={handleChange}
                className="flex-1 px-4 py-3 border-2 rounded-xl focus:ring-2 transition-all duration-200"
                style={{ backgroundColor: UI_COLORS.FORMS.INPUT.background, borderColor: errors.map_url ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border, color: UI_COLORS.FORMS.INPUT.text }}
                placeholder={strings.forms.fields.mapUrl.placeholder}
              />
              <button
                type="button"
                onClick={openPreviewMap}
                className="px-4 py-2 rounded-lg font-semibold"
                style={{ backgroundColor: UI_COLORS.BUTTONS.SECONDARY.background, color: UI_COLORS.BUTTONS.SECONDARY.text }}
              >
                {strings.forms.buttons.previewMap}
              </button>
            </div>
            {errors.map_url && <p className="text-sm font-medium" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.map_url}</p>}
          </div>

          {submitError && (
            <div
              className="rounded-xl px-4 py-3 text-sm font-medium"
              role="alert"
              style={{
                backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.ERROR, 0.08),
                color: UI_COLORS.STATUS.ERROR,
                border: `1px solid ${colorHelpers.withOpacity(UI_COLORS.STATUS.ERROR, 0.3)}`,
              }}
            >
              {submitError}
            </div>
          )}

          <div className="flex items-center justify-end space-x-4 pt-6">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-6 py-3 rounded-xl font-semibold transition-all duration-200 hover:scale-105 disabled:opacity-50 disabled:transform-none"
              style={{ backgroundColor: 'transparent', color: UI_COLORS.TEXT.MUTED, border: `2px solid ${UI_COLORS.BORDERS.MEDIUM}` }}
              onMouseEnter={(e) => { if (!loading) e.currentTarget.style.backgroundColor = colorHelpers.withOpacity(UI_COLORS.TEXT.MUTED, 0.1); }}
              onMouseLeave={(e) => { if (!loading) e.currentTarget.style.backgroundColor = 'transparent'; }}
            >
              {strings.forms.buttons.cancel}
            </button>

            <button
              type="submit"
              disabled={loading}
              className="px-6 py-3 rounded-xl font-semibold transition-all duration-200 hover:scale-105 disabled:opacity-50 disabled:transform-none"
              style={{ backgroundColor: loading ? UI_COLORS.BUTTONS.PRIMARY.loading.background : UI_COLORS.BUTTONS.PRIMARY.background, color: loading ? UI_COLORS.BUTTONS.PRIMARY.loading.text : UI_COLORS.BUTTONS.PRIMARY.text }}
            >
              {loading ? (
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>{strings.forms.buttons.submit.loading}</span>
                </div>
              ) : (
                <div className="flex items-center space-x-2">
                  <FontAwesomeIcon icon={faSave} />
                  <span>{strings.forms.buttons.submit.add}</span>
                </div>
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Map Modal */}
      <Map
        isOpen={showMapModal}
        onClose={closeMapModal}
        onLocationSelect={handleLocationSelect}
        initialLocation={{
          country: formData.country,
          state: formData.state,
          city: formData.city,
          delivery_location: formData.delivery_location,
          // Parse coordinates if available
          lat: (() => {
            const coords = parseLatLngFromForm();
            return coords?.lat;
          })(),
          lng: (() => {
            const coords = parseLatLngFromForm();
            return coords?.lng;
          })(),
        }}
        title="Select Delivery Location"
        height="70vh"
      />
    </div>
  );
};

export default LocationForm;
